import React, { useState, useEffect, useMemo } from "react";
import {
  Dropdown,
  DefaultButton,
  PrimaryButton,
  Dialog,
  DialogType,
  Panel,
  PanelType,
  Toggle,
  SpinButton
} from "@fluentui/react";
import Detectors from "../../Assets/icons/Detectors.svg";
import Modules from "../../Assets/icons/Modules.svg";
import Zones from "../../Assets/icons/Zones.svg";
import Logic from "../../Assets/icons/Logic.svg";
import OutputZones from "../../Assets/icons/OutputZones.svg";
import Dependency from "../../Assets/icons/Dependency.svg";
import Map from "../../Assets/icons/Map.svg";
import "../../Styles/DeviceZoneFrame.css";
import Axios from "axios";
import { Icon } from "@fluentui/react/lib/Icon";

const DeviceZoneFrame = ({ setCurrentFrame, project, panelVersion }) => {
  const [currentTime, setCurrentTime] = useState("");
  const [dependencyType, setDependencyType] = useState(0);
  const [field1, setField1] = useState(1);
  const [field2, setField2] = useState(1);
  const [field3, setField3] = useState(0);
  const [field4, setField4] = useState(0);
  const [field5, setField5] = useState(0);
  const [field6, setField6] = useState(0);
  const [field7, setField7] = useState(0);
  const [field8, setField8] = useState(0);
  const [field9, setField9] = useState(0);
  const [field10, setField10] = useState(0);
  const [field11, setField11] = useState(0);
  const [field12, setField12] = useState(0);
  const [field13, setField13] = useState(0);
  const [field14, setField14] = useState(0);
  const [field15, setField15] = useState(0);
  const [field16, setField16] = useState(0);
  const [field17, setField17] = useState(0);
  const [field18, setField18] = useState(0);
  const [field19, setField19] = useState(0);

  const panelNumber = project?.panel?.VT5000_CONFIG?.GeneralParameters?.vtnetNetworkPanelNb ?? 0;
  const rawPanelAddress =
    project?.panel?.VT5000_CONFIG?.GeneralParameters?.vtnetPanelAddress ?? 0;
  // Panel address should be 1..64; otherwise treat as unknown (0)
  const panelAddress =
    rawPanelAddress >= 1 && rawPanelAddress <= panelNumber ? rawPanelAddress : 0;

  const [isDevicesDialogOpen, setIsDevicesDialogOpen] = useState(false);
  const [selectedDependency, setSelectedDependency] = useState(0);

  const panelNumberOptions = useMemo(() => {
    // If panelAddress is unknown (0), force "Local" only.
    if (panelAddress === 0) {
      return [{ key: 0, text: "Local" }];
    }

    // 1..64, but display the local panel address as "Local"
    return Array.from({ length: panelNumber }, (_, i) => {
      const value = i + 1;
      return { key: value, text: value === panelAddress ? "Local" : `${value}` };
    });
  }, [panelAddress, panelNumber]);

  // Backward compatibility: old configs may store "Local" as 0.
  // If we know the panelAddress, convert 0 -> panelAddress so it saves correctly.
  useEffect(() => {
    if (panelAddress > 0 && field11 === 0) {
      setField11(panelAddress);
    }
  }, [panelAddress, field11]);


  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      let formattedTime = now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).replace(/,/g, ""); // Remove comma between date and time

      formattedTime = formattedTime.replace(/(\d{4})\//, "$1 "); // Remove '/' after year
      setCurrentTime(formattedTime);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // UI defaults (display only). We keep underlying values as 0 until the user clicks Save.
  const isType12 = dependencyType === 1 || dependencyType === 2;
  const isType3 = dependencyType === 3;

  // Type 1/2
  const displayInhibitionPeriod = isType12 && field1 === 0 ? 10 : field1;
  const displaySmartVerifyPeriod = isType12 && field2 === 0 ? 5 : field2;

  // Included Zones:
  // - Type 1/2 uses field3/field4
  // - Type 3 uses field1/field2
  const displayIncludedZonesFrom12 =
    isType12 && field3 === 0 && field4 === 0 ? 1 : (isType12 && field3 === 0 ? 1 : field3);
  const displayIncludedZonesTo12 =
    isType12 && field3 === 0 && field4 === 0 ? 1 : (isType12 && field4 === 0 ? displayIncludedZonesFrom12 : field4);

  const displayIncludedZonesFrom3 =
    isType3 && field1 === 0 && field2 === 0 ? 1 : (isType3 && field1 === 0 ? 1 : field1);
  const displayIncludedZonesTo3 =
    isType3 && field1 === 0 && field2 === 0 ? 1 : (isType3 && field2 === 0 ? displayIncludedZonesFrom3 : field2);

  // Second Fire Output (Type 3 only)
  const displaySecondFireR1From =
    isType3 && field9 === 0 && field10 === 0 ? 1 : (isType3 && field9 === 0 ? 1 : field9);
  const displaySecondFireR1To =
    isType3 && field9 === 0 && field10 === 0 ? 1 : (isType3 && field10 === 0 ? displaySecondFireR1From : field10);

  const displaySecondFireR2From =
    isType3 ? field12 : field12;
  const displaySecondFireR2To =
    isType3 ? field13 : field13;

  const displaySecondFireR3From =
    isType3 ? field14 : field14;
  const displaySecondFireR3To =
    isType3 ? field15 : field15;

  const loadDependencyToState = (dep) => {
    const safeDep = dep ?? null;

    setDependencyType(safeDep?.dependencyType ?? 0);
    setField1(safeDep?.information_field1 ?? 0);
    setField2(safeDep?.information_field2 ?? 0);
    setField3(safeDep?.information_field3 ?? 0);
    setField4(safeDep?.information_field4 ?? 0);
    setField5(safeDep?.information_field5 ?? 0);
    setField6(safeDep?.information_field6 ?? 0);
    setField7(safeDep?.information_field7 ?? 0);
    setField8(safeDep?.information_field8 ?? 0);
    setField9(safeDep?.information_field9 ?? 0);
    setField10(safeDep?.information_field10 ?? 0);
    setField11(safeDep?.information_field11 ?? 0);
    setField12(safeDep?.information_field12 ?? 0);
    setField13(safeDep?.information_field13 ?? 0);
    setField14(safeDep?.information_field14 ?? 0);
    setField15(safeDep?.information_field15 ?? 0);
    setField16(safeDep?.information_field16 ?? 0);
    setField17(safeDep?.information_field17 ?? 0);
    setField18(safeDep?.information_field18 ?? 0);
    setField19(safeDep?.information_field19 ?? 0);
  };

  // When the dialog opens (or the selected dependency changes), reload values from the panel config.
  // This prevents "stale" UI values after saving (e.g., switching to None, saving, then reopening and switching to A).
  useEffect(() => {
    if (!isDevicesDialogOpen) return;
    const dep = project?.panel?.VT5000_CONFIG?.Dependency?.[selectedDependency];
    loadDependencyToState(dep);
  }, [isDevicesDialogOpen, selectedDependency, project]);

  const handleSave = () => {
    if (project?.panel?.VT5000_CONFIG?.Dependency) {
      const dep = project.panel.VT5000_CONFIG.Dependency[selectedDependency];

      dep.dependencyType = dependencyType;

      // Save defaults ONLY when the user clicks Save.
      // (UI may display defaults while underlying values are still 0.)
      let saveField1 = field1;
      let saveField2 = field2;
      let saveField3 = field3;
      let saveField4 = field4;
      let saveField5 = field5;
      let saveField6 = field6;
      let saveField7 = field7;
      let saveField8 = field8;
      let saveField9 = field9;
      let saveField10 = field10;
      let saveField11 = field11;
      let saveField12 = field12;
      let saveField13 = field13;
      let saveField14 = field14;
      let saveField15 = field15;
      let saveField16 = field16;
      let saveField17 = field17;
      let saveField18 = field18;
      let saveField19 = field19;

      // If type is None, clear everything.
      if (dependencyType === 0) {
        saveField1 = 0;
        saveField2 = 0;
        saveField3 = 0;
        saveField4 = 0;
        saveField5 = 0;
        saveField6 = 0;
        saveField7 = 0;
        saveField8 = 0;
        saveField9 = 0;
        saveField10 = 0;
        saveField11 = 0;
        saveField12 = 0;
        saveField13 = 0;
        saveField14 = 0;
        saveField15 = 0;
        saveField16 = 0;
        saveField17 = 0;
        saveField18 = 0;
        saveField19 = 0;
      }

      // Type A/B
      if (dependencyType === 1 || dependencyType === 2) {
        if (saveField1 === 0) saveField1 = 10; // Inhibition Period default
        if (saveField2 === 0) saveField2 = 5;  // Smart Verify Period default
        if (saveField3 === 0 && saveField4 === 0) {
          saveField3 = 1; // Included Zones default
          saveField4 = 1;
        }
      }

      // Type C
      if (dependencyType === 3) {
        if (saveField1 === 0 && saveField2 === 0) {
          saveField1 = 1; // Included Zones default
          saveField2 = 1;
        }
        if (saveField9 === 0 && saveField10 === 0) {
          saveField9 = 1; // Second Fire Output default
          saveField10 = 1;
        }
      }

      // Panel Number (field11)
      // - If type is None: keep 0
      // - If field11 is 0 and panelAddress is known: save panelAddress
      // - If panelAddress is 0: keep 0
      if (dependencyType === 0) {
        saveField11 = 0;
      } else if (panelAddress > 0 && saveField11 === 0) {
        saveField11 = panelAddress;
      }

      dep.information_field1 = saveField1;
      dep.information_field2 = saveField2;
      dep.information_field3 = saveField3;
      dep.information_field4 = saveField4;
      dep.information_field5 = saveField5;
      dep.information_field6 = saveField6;
      dep.information_field7 = saveField7;
      dep.information_field8 = saveField8;
      dep.information_field9 = saveField9;
      dep.information_field10 = saveField10;
      dep.information_field11 = saveField11;
      dep.information_field12 = saveField12;
      dep.information_field13 = saveField13;
      dep.information_field14 = saveField14;
      dep.information_field15 = saveField15;
      dep.information_field16 = saveField16;
      dep.information_field17 = saveField17;
      dep.information_field18 = saveField18;
      dep.information_field19 = saveField19;

      // Keep UI state consistent with what we just saved (prevents stale values after switching types).
      setField1(saveField1);
      setField2(saveField2);
      setField3(saveField3);
      setField4(saveField4);
      setField5(saveField5);
      setField6(saveField6);
      setField7(saveField7);
      setField8(saveField8);
      setField9(saveField9);
      setField10(saveField10);
      setField11(saveField11);
      setField12(saveField12);
      setField13(saveField13);
      setField14(saveField14);
      setField15(saveField15);
      setField16(saveField16);
      setField17(saveField17);
      setField18(saveField18);
      setField19(saveField19);

      const userEmail = localStorage.getItem('userEmail');
      Axios.post(`${process.env.REACT_APP_BACKEND_URL}/update_panel_dependency`, {
        projectId: project.project_id,
        panelContentId: project.panel.VT5000_CONFIG.panelContent_id,
        dependency: project.panel.VT5000_CONFIG.Dependency,
        email: userEmail,
      })
        .then((response) => {
          if (response.data.success) {
            console.log("Dependency updated successfully");
            setIsDevicesDialogOpen(false)
          } else {
            console.error("Failed to update Dependency");
          }
        })
        .catch((error) => {
          console.error("Error updating Dependency:", error);
        });
    }
  };

  return (
    <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div style={{ fontSize: 40, width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ textAlign: "left", flex: 1 }}>Device/Zone</span>
        <span style={{ textAlign: "right", flex: 1 }}>{currentTime}</span>
      </div>
      <hr style={{ width: "100%", border: "1px solid black", margin: "5px 0" }} />
      {panelVersion === "2.7.F9" && <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1px", width: "98%", height: "72%", textAlign: "center" }}>
        {[{ icon: Detectors, text: "Detectors", onClick: () => setCurrentFrame("detectors") },
        { icon: Modules, text: "Modules", onClick: () => setCurrentFrame("modules") },
        { icon: Zones, text: "Zones", onClick: () => setCurrentFrame("zones") },
        { icon: Logic, text: "Logic Functions", onClick: () => setCurrentFrame("logicFunctions") },
        { icon: OutputZones, text: "Output Zones", onClick: () => setCurrentFrame("outputZones") },
        { icon: Dependency, text: "Smart Verify", onClick: () => setIsDevicesDialogOpen(true) },].map((btn, index) => (
          <button key={index} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px", fontSize: "30px", width: "100%", background: btn.disabled ? "#ccc" : "none", borderRadius: "5px", cursor: btn.disabled ? "not-allowed" : "pointer", opacity: btn.disabled ? 0.5 : 1, }}
            onClick={btn.disabled ? undefined : btn.onClick}
            disabled={btn.disabled}
          >
            <img src={btn.icon} alt={btn.text} style={{ width: "70px", height: "70px", marginBottom: "10px" }} />
            {btn.text}
          </button>
        ))}
      </div>}
      {panelVersion === "2.0.F7" && <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1px", width: "98%", height: "72%", textAlign: "center" }}>
        {[{ icon: Detectors, text: "Detectors", onClick: () => setCurrentFrame("detectors") },
        { icon: Modules, text: "Modules", onClick: () => setCurrentFrame("modules") },
        { icon: Zones, text: "Zones", onClick: () => setCurrentFrame("zones") },
        { icon: Logic, text: "Logic Functions", onClick: () => setCurrentFrame("logicFunctions") },
        { icon: OutputZones, text: "Output Zones", onClick: () => setCurrentFrame("outputZones") },
        { icon: Map, text: "Device Map", disabled: true },].map((btn, index) => (
          <button key={index} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px", fontSize: "30px", width: "100%", background: btn.disabled ? "#ccc" : "none", borderRadius: "5px", cursor: btn.disabled ? "not-allowed" : "pointer", opacity: btn.disabled ? 0.5 : 1, }}
            onClick={btn.disabled ? undefined : btn.onClick}
            disabled={btn.disabled}
          >
            <img src={btn.icon} alt={btn.text} style={{ width: "70px", height: "70px", marginBottom: "10px" }} />
            {btn.text}
          </button>
        ))}
      </div>}
      <Dialog
        hidden={!isDevicesDialogOpen}
        onDismiss={() => setIsDevicesDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
        }}
        minWidth={"1200px"}
      >
        <div>
          <div className="devices-content">
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", width: "100%", marginTop: "10px" }}>
              <span style={{ fontSize: "30px" }}>Smart Verify Type</span>
              <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
                {[
                  { label: "None", value: 0 },
                  { label: "A", value: 1 },
                  { label: "B", value: 2 },
                  { label: "C", value: 3 }
                ].map(({ label, value }) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "150px",
                      height: "60px",
                      border: "2px solid black",
                      borderRadius: "5px",
                      cursor: "pointer",
                      background: dependencyType === value ? "#C00D0D" : "#FFF"
                    }}
                    onClick={() => setDependencyType(value)} // toggle logic
                  >
                    <span style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: dependencyType === value ? "#FFF" : "#000"
                    }}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
              {dependencyType === 1 && (
                <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "80%", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "30px" }}>Inhibition Period</span>
                  <Dropdown
                    options={Array.from({ length: 5 }, (_, i) => {
                      const value = (i + 1) * 10;      // 10, 20, ..., 50
                      return { key: value, text: `${value} secs` };
                    })}
                    selectedKey={displayInhibitionPeriod}
                    onChange={(e, option) => setField1(option.key)}
                    onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />}
                    styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                  />
                </div>
              )}
              {dependencyType === 2 && (
                <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "80%", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "30px" }}>Inhibition Period</span>
                  <Dropdown
                    options={Array.from({ length: 23 }, (_, i) => {
                      const value = (i + 1) * 10;      // 10, 20, ..., 230
                      return { key: value, text: `${value} secs` };
                    })}
                    selectedKey={displayInhibitionPeriod}
                    onChange={(e, option) => setField1(option.key)}
                    onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />}
                    styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                  />
                </div>
              )}
              {(dependencyType === 1 || dependencyType === 2) && (<div style={{ display: "flex", alignItems: "center", gap: "10px", width: "80%", justifyContent: "space-between" }}>
                <span style={{ fontSize: "30px" }}>Smart Verify Period</span>
                <Dropdown
                  options={Array.from({ length: 30 }, (_, i) => ({
                    key: i + 1,
                    text: `${i + 1} mins`
                  }))}
                  selectedKey={displaySmartVerifyPeriod}
                  onChange={(e, option) => setField2(option.key)}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
              </div>)}
              {(dependencyType === 1 || dependencyType === 2) && (<div style={{ display: "flex", alignItems: "center", gap: "10px", width: "80%", justifyContent: "space-between" }}>
                <span style={{ fontSize: "30px", marginRight: "20%" }}>Included Zones</span>
                <Dropdown
                  options={Array.from({ length: 192 }, (_, i) => ({ key: i + 1, text: (i + 1).toString() }))}
                  selectedKey={displayIncludedZonesFrom12}
                  onChange={(e, option) => {
                    const newField3 = option.key;
                    setField3(newField3);
                    if (newField3 > field4) {
                      setField4(newField3); // auto-adjust field4
                    }
                  }}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
                <span style={{ fontSize: "30px" }}>To</span>
                <Dropdown
                  options={Array.from({ length: (193 - displayIncludedZonesFrom12) }, (_, i) => ({ key: (i + displayIncludedZonesFrom12), text: (i + displayIncludedZonesFrom12).toString() }))}
                  selectedKey={displayIncludedZonesTo12}
                  onChange={(e, option) => setField4(option.key)}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
              </div>)}
              {(dependencyType === 1 || dependencyType === 2) && (<div style={{ display: "flex", alignItems: "center", gap: "10px", width: "80%", justifyContent: "space-between" }}>
                <span style={{ fontSize: "30px", marginRight: "18.5%" }}>First Fire Output</span>
                <Dropdown
                  options={Array.from({ length: 256 }, (_, i) => ({ key: i, text: i.toString() }))}
                  selectedKey={field5}
                  onChange={(e, option) => {
                    const newField5 = option.key;
                    setField5(newField5);
                    if (newField5 > field6) {
                      setField6(newField5); // auto-adjust field4
                    }
                  }}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
                <span style={{ fontSize: "30px" }}>To</span>
                <Dropdown
                  options={Array.from({ length: (255 - field5 + 1) }, (_, i) => ({ key: (i + field5), text: (i + field5).toString() }))}
                  selectedKey={field6}
                  onChange={(e, option) => setField6(option.key)}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
              </div>)}
              {dependencyType === 3 && (<div style={{ display: "flex", alignItems: "center", gap: "10px", width: "80%", justifyContent: "space-between" }}>
                <span style={{ fontSize: "30px", marginRight: "20%" }}>Included Zones</span>
                <Dropdown
                  options={Array.from({ length: 192 }, (_, i) => ({ key: i + 1, text: (i + 1).toString() }))}
                  selectedKey={displayIncludedZonesFrom3}
                  onChange={(e, option) => {
                    const newField1 = option.key;
                    setField1(newField1);
                    if (newField1 > field2) {
                      setField2(newField1); // auto-adjust field4
                    }
                  }}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
                <span style={{ fontSize: "30px" }}>To</span>
                <Dropdown
                  options={Array.from({ length: (193 - displayIncludedZonesFrom3) }, (_, i) => ({ key: (i + displayIncludedZonesFrom3), text: (i + displayIncludedZonesFrom3).toString() }))}
                  selectedKey={displayIncludedZonesTo3}
                  onChange={(e, option) => setField2(option.key)}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
              </div>)}
              {dependencyType === 3 && (
                <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "80%", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "30px" }}>Panel Number</span>
                  <Dropdown
                    options={panelNumberOptions}
                    selectedKey={panelAddress === 0 ? 0 : (field11 === 0 ? panelAddress : field11)}
                    onChange={(e, option) => {
                      if (panelAddress === 0) return;
                      setField11(option.key);
                    }}
                    disabled={panelAddress === 0}
                    onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />}
                    styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                  />
                </div>
              )}
              {dependencyType === 3 && (
                <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "80%", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "30px" }}>1st Fire D1</span>
                  <Dropdown
                    options={Array.from({ length: 43 }, (_, i) => {
                      const value = i * 10;      // 0, 10, 20, ..., 420
                      return { key: value, text: `${value} secs` };
                    })}
                    selectedKey={field3}
                    onChange={(e, option) => setField3(option.key)}
                    onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />}
                    styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                  />
                </div>
              )}
              {dependencyType === 3 && (
                <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "80%", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "30px" }}>1st Fire D2</span>
                  <Dropdown
                    options={Array.from({ length: 43 }, (_, i) => {
                      const value = i * 10;      // 0, 10, 20, ..., 420
                      return { key: value, text: `${value} secs` };
                    })}
                    selectedKey={field4}
                    onChange={(e, option) => setField4(option.key)}
                    onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />}
                    styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                  />
                </div>
              )}
              {dependencyType === 3 && (<div style={{ display: "flex", alignItems: "center", gap: "10px", width: "80%", justifyContent: "space-between" }}>
                <span style={{ fontSize: "30px", marginRight: "13.6%" }}>1st Fire Out (D1) R1</span>
                <Dropdown
                  options={Array.from({ length: 256 }, (_, i) => ({ key: i, text: i.toString() }))}
                  selectedKey={field5}
                  onChange={(e, option) => {
                    const newField5 = option.key;
                    setField5(newField5);
                    if (newField5 > field6) {
                      setField6(newField5);
                    }
                  }}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
                <span style={{ fontSize: "30px" }}>To</span>
                <Dropdown
                  options={Array.from({ length: (256 - field5) }, (_, i) => ({ key: (i + field5), text: (i + field5).toString() }))}
                  selectedKey={field6}
                  onChange={(e, option) => setField6(option.key)}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
              </div>)}
              {dependencyType === 3 && (<div style={{ display: "flex", alignItems: "center", gap: "10px", width: "80%", justifyContent: "space-between" }}>
                <span style={{ fontSize: "30px", marginRight: "13.6%" }}>1st Fire Out (D2) R1</span>
                <Dropdown
                  options={Array.from({ length: 256 }, (_, i) => ({ key: i, text: i.toString() }))}
                  selectedKey={field7}
                  onChange={(e, option) => {
                    const newField7 = option.key;
                    setField7(newField7);
                    if (newField7 > field8) {
                      setField8(newField7);
                    }
                  }}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
                <span style={{ fontSize: "30px" }}>To</span>
                <Dropdown
                  options={Array.from({ length: (256 - field7) }, (_, i) => ({ key: (i + field7), text: (i + field7).toString() }))}
                  selectedKey={field8}
                  onChange={(e, option) => setField8(option.key)}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
              </div>)}
              {dependencyType === 3 && (<div style={{ display: "flex", alignItems: "center", gap: "10px", width: "80%", justifyContent: "space-between" }}>
                <span style={{ fontSize: "30px", marginRight: "13.6%" }}>1st Fire Out (D1) R2</span>
                <Dropdown
                  options={Array.from({ length: 256 }, (_, i) => ({ key: i, text: i.toString() }))}
                  selectedKey={field16}
                  onChange={(e, option) => {
                    const newField16 = option.key;
                    setField16(newField16);
                    if (newField16 > field17) {
                      setField17(newField16);
                    }
                  }}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
                <span style={{ fontSize: "30px" }}>To</span>
                <Dropdown
                  options={Array.from({ length: (256 - field16) }, (_, i) => ({ key: (i + field16), text: (i + field16).toString() }))}
                  selectedKey={field17}
                  onChange={(e, option) => setField17(option.key)}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
              </div>)}
              {dependencyType === 3 && (<div style={{ display: "flex", alignItems: "center", gap: "10px", width: "80%", justifyContent: "space-between" }}>
                <span style={{ fontSize: "30px", marginRight: "13.6%" }}>1st Fire Out (D2) R2</span>
                <Dropdown
                  options={Array.from({ length: 256 }, (_, i) => ({ key: i, text: i.toString() }))}
                  selectedKey={field18}
                  onChange={(e, option) => {
                    const newField18 = option.key;
                    setField18(newField18);
                    if (newField18 > field19) {
                      setField19(newField18);
                    }
                  }}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
                <span style={{ fontSize: "30px" }}>To</span>
                <Dropdown
                  options={Array.from({ length: (256 - field18) }, (_, i) => ({ key: (i + field18), text: (i + field18).toString() }))}
                  selectedKey={field19}
                  onChange={(e, option) => setField19(option.key)}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
              </div>)}
              {dependencyType === 3 && (<div style={{ display: "flex", alignItems: "center", gap: "10px", width: "80%", justifyContent: "space-between" }}>
                <span style={{ fontSize: "30px", marginRight: "19.4%" }}>2nd Fire Out R1</span>
                <Dropdown
                  options={Array.from({ length: 255 }, (_, i) => ({ key: i + 1, text: (i + 1).toString() }))}
                  selectedKey={displaySecondFireR1From}
                  onChange={(e, option) => {
                    const newField9 = option.key;
                    setField9(newField9);
                    if (newField9 > field10) {
                      setField10(newField9);
                    }
                  }}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
                <span style={{ fontSize: "30px" }}>To</span>
                <Dropdown
                  options={Array.from({ length: (256 - displaySecondFireR1From) }, (_, i) => ({ key: (i + displaySecondFireR1From), text: (i + displaySecondFireR1From).toString() }))}
                  selectedKey={displaySecondFireR1To}
                  onChange={(e, option) => setField10(option.key)}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
              </div>)}
              {dependencyType === 3 && (<div style={{ display: "flex", alignItems: "center", gap: "10px", width: "80%", justifyContent: "space-between" }}>
                <span style={{ fontSize: "30px", marginRight: "19.4%" }}>2nd Fire Out R2</span>
                <Dropdown
                  options={Array.from({ length: 256 }, (_, i) => ({ key: i, text: i.toString() }))}
                  selectedKey={displaySecondFireR2From}
                  onChange={(e, option) => {
                    const newField12 = option.key;
                    setField12(newField12);
                    if (newField12 > field13) {
                      setField13(newField12);
                    }
                  }}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
                <span style={{ fontSize: "30px" }}>To</span>
                <Dropdown
                  options={Array.from({ length: (256 - displaySecondFireR2From) }, (_, i) => ({ key: (i + displaySecondFireR2From), text: (i + displaySecondFireR2From).toString() }))}
                  selectedKey={displaySecondFireR2To}
                  onChange={(e, option) => setField13(option.key)}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
              </div>)}
              {dependencyType === 3 && (<div style={{ display: "flex", alignItems: "center", gap: "10px", width: "80%", justifyContent: "space-between" }}>
                <span style={{ fontSize: "30px", marginRight: "19.4%" }}>2nd Fire Out R3</span>
                <Dropdown
                  options={Array.from({ length: 256 }, (_, i) => ({ key: i, text: i.toString() }))}
                  selectedKey={displaySecondFireR3From}
                  onChange={(e, option) => {
                    const newField14 = option.key;
                    setField14(newField14);
                    if (newField14 > field15) {
                      setField15(newField14);
                    }
                  }}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
                <span style={{ fontSize: "30px" }}>To</span>
                <Dropdown
                  options={Array.from({ length: (256 - displaySecondFireR3From) }, (_, i) => ({ key: (i + displaySecondFireR3From), text: (i + displaySecondFireR3From).toString() }))}
                  selectedKey={displaySecondFireR3To}
                  onChange={(e, option) => setField15(option.key)}
                  onRenderCaretDown={() => <Icon iconName="ChevronDown" styles={{ root: { fontSize: 20 } }} />} // Custom caret down icon
                  styles={{ dropdown: { width: 150 }, callout: { maxHeight: 200 } }}
                />
              </div>)}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "20px" }}>
              <div style={{ display: "flex", gap: "10px" }}>
                <PrimaryButton text="Save" onClick={() => handleSave()} />
                <DefaultButton text="Back" onClick={() => setIsDevicesDialogOpen(false)} />
              </div>
            </div>
            <div className="dependency-buttons-container">
              <div className="dependency-buttons-frame">
                {Array.from({ length: 105 }, (_, index) => {
                  return (
                    <button
                      key={index}
                      className={`dependency-button ${(project?.panel?.VT5000_CONFIG?.Dependency?.[index]?.dependencyType !== 0) ? "programmed" : "not-programmed"} ${(index === selectedDependency) ? "selected" : ""}`}
                      onClick={() => {
                        setSelectedDependency(index);
                        const dep = project?.panel?.VT5000_CONFIG?.Dependency?.[index];
                        loadDependencyToState(dep);
                      }}
                    >
                      <span>{index + 1}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Dialog>
      <div style={{ display: "flex", width: "98%", justifyContent: "space-evenly", position: "absolute", bottom: "2%" }}>
        <button style={{ flex: 1, padding: "0px", fontSize: "40px", margin: "0 40% 0 0", background: "none", borderRadius: "5px" }} onClick={() => setCurrentFrame("main")}>Logout</button>
        <button style={{ flex: 1, padding: "0px", fontSize: "40px", margin: "0 0px", background: "none", borderRadius: "5px" }} onClick={() => setCurrentFrame("configure")}>Back</button>
      </div>
    </div>
  );
};

export default DeviceZoneFrame;